import axios from 'axios';
import { setCachedData, getCachedData, generateCacheKey, clearCacheItem } from '../utils/cacheManager';

// Use empty string for same-origin API calls (nginx proxies /api/ to backend)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const FINANCIAL_CACHE_KEY = 'financial_data_v36';

class EnhancedFinancialDataService {
  constructor(apiBaseUrl = API_BASE_URL) {
    this.apiBaseUrl = apiBaseUrl;
    this.cacheDuration = {
      financial: 30 * 60 * 1000, // 30 minutes
      statements: 60 * 60 * 1000, // 1 hour
      metrics: 15 * 60 * 1000 // 15 minutes
    };
  }

  getFirstFiniteValue(data, keys = []) {
    for (const key of keys) {
      const value = data?.[key];
      const parsed = typeof value === 'number' ? value : parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  normalizeCurrencyCode(value) {
    const raw = String(value || '').trim().toUpperCase();
    if (!raw) return 'USD';
    if (raw === 'NT$' || raw === 'NTD' || raw === 'TWD$' || raw === 'TW$') return 'TWD';
    if (raw === 'US$' || raw === 'USD$') return 'USD';
    if (raw === 'RMB') return 'CNY';
    const compact = raw.replace(/[^A-Z]/g, '');
    if (compact.includes('TWD')) return 'TWD';
    if (compact.includes('USD')) return 'USD';
    return compact.length === 3 ? compact : raw;
  }

  normalizePeriodType(periodType) {
    return String(periodType || '').trim().toLowerCase() === 'annual' ? 'annual' : 'quarterly';
  }

  // Format date for API - avoid timezone issues by parsing directly
  formatDateForApi(dateString) {
    if (!dateString) return '';
    
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse ISO date string without timezone conversion
    const date = new Date(dateString);
    // Use UTC methods to avoid timezone shifts
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  }

  // Get financial data (revenue, operating income)
  async getFinancialData(ticker, forceRefresh = false, periodType = 'quarterly') {
    if (!ticker) return [];

    const normalizedPeriodType = this.normalizePeriodType(periodType);
    const cacheKey = generateCacheKey(`${FINANCIAL_CACHE_KEY}_${normalizedPeriodType}`, ticker);
    let cachedData = forceRefresh ? null : getCachedData(cacheKey);

    // Client cache can keep a pre-refresh series (e.g. MU ending Feb) even after
    // SEC has a newer 10-Q. Invalidate when latest period is older than ~100 days.
    if (cachedData && !forceRefresh) {
      const cachedRows = Array.isArray(cachedData) ? cachedData : (cachedData.data || cachedData);
      if (Array.isArray(cachedRows) && cachedRows.length > 0) {
        const maxPeriodTs = cachedRows
          .map((row) => Date.parse(String(row?.period || '')))
          .filter((ts) => Number.isFinite(ts))
          .sort((a, b) => b - a)[0];
        const ageDays = Number.isFinite(maxPeriodTs)
          ? (Date.now() - maxPeriodTs) / (24 * 60 * 60 * 1000)
          : Infinity;
        const maxAgeDays = normalizedPeriodType === 'annual' ? 400 : 100;
        if (ageDays > maxAgeDays) {
          clearCacheItem(cacheKey);
          cachedData = null;
          forceRefresh = true;
        }
      }
    }
    
    if (cachedData) {
      console.log(`Using cached financial data for ${ticker}`);
      // Handle both new format { currency, data } and legacy array format
      const cachedRows = Array.isArray(cachedData) ? cachedData : (cachedData.data || cachedData);
      if (Array.isArray(cachedRows) && cachedRows.length > 0) {
        return cachedRows;
      }
      // Empty cached payloads can happen during transient backend issues.
      // Drop and refetch so users recover without manual cache clears.
      clearCacheItem(cacheKey);
    }
    
    try {
      console.log(`Fetching financial data for ${ticker} from API`);
      let response = await axios.get(`${this.apiBaseUrl}/api/financial-data/${ticker}`, {
        params: {
          ...(forceRefresh ? { force: 1 } : {}),
          periodType: normalizedPeriodType,
        },
      });

      const toRows = (payload) => {
        const isNewFormat = payload && payload.currency !== undefined;
        return isNewFormat ? payload.data : payload;
      };

      let rows = toRows(response.data);
      if ((!Array.isArray(rows) || rows.length === 0) && !forceRefresh) {
        response = await axios.get(`${this.apiBaseUrl}/api/financial-data/${ticker}`, {
          params: { force: 1, periodType: normalizedPeriodType },
        });
        rows = toRows(response.data);
      }
      
      // Cache the data (preserve full response for currency info)
      setCachedData(cacheKey, response.data, this.cacheDuration.financial);
      
      // Handle new format { currency, data } or legacy array format
      const isNewFormat = response.data && response.data.currency !== undefined;
      return isNewFormat ? response.data.data : response.data;
    } catch (error) {
      console.error(`Error fetching financial data for ${ticker}:`, error);
      throw error;
    }
  }

  // Get currency for a ticker (must call getFinancialData first)
  getCurrency(ticker, periodType = 'quarterly') {
    if (!ticker) return 'USD';
    const normalizedPeriodType = this.normalizePeriodType(periodType);
    const cacheKey = generateCacheKey(`${FINANCIAL_CACHE_KEY}_${normalizedPeriodType}`, ticker);
      const cachedData = getCachedData(cacheKey);
    if (cachedData && cachedData.currency) {
      return this.normalizeCurrencyCode(cachedData.currency);
    }
    return 'USD';
  }

  getFinancialDataMeta(ticker, periodType = 'quarterly') {
    if (!ticker) return null;
    const normalizedPeriodType = this.normalizePeriodType(periodType);
    const cacheKey = generateCacheKey(`${FINANCIAL_CACHE_KEY}_${normalizedPeriodType}`, ticker);
    const cachedData = getCachedData(cacheKey);
    if (!cachedData || Array.isArray(cachedData)) {
      return null;
    }
    return cachedData.dataQuality || null;
  }

  // Get income statement for a specific period
  async getIncomeStatement(ticker, period) {
    if (!ticker || !period) return null;
    
    const formattedPeriod = this.formatDateForApi(period);
    const cacheKey = generateCacheKey('income_statement_v23', ticker, formattedPeriod);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached income statement for ${ticker}, period ${formattedPeriod}`);
      return cachedData;
    }
    
    try {
      console.log(`Fetching income statement for ${ticker}, period ${formattedPeriod}`);
      const response = await axios.get(`${this.apiBaseUrl}/api/income-statement/${ticker}/${formattedPeriod}`);
      
      // Cache the data
      setCachedData(cacheKey, response.data, this.cacheDuration.statements);
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching income statement for ${ticker}, period ${formattedPeriod}:`, error);
      throw error;
    }
  }

  // Get cash flow statement for a specific period
  async getCashFlow(ticker, period) {
    if (!ticker || !period) return null;
    
    const formattedPeriod = this.formatDateForApi(period);
    const cacheKey = generateCacheKey('cash_flow_v6', ticker, formattedPeriod);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached cash flow for ${ticker}, period ${formattedPeriod}`);
      return cachedData;
    }
    
    try {
      console.log(`Fetching cash flow for ${ticker}, period ${formattedPeriod}`);
      const response = await axios.get(`${this.apiBaseUrl}/api/cash-flow/${ticker}/${formattedPeriod}`);
      
      // Cache the data
      setCachedData(cacheKey, response.data, this.cacheDuration.statements);
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching cash flow for ${ticker}, period ${formattedPeriod}:`, error);
      throw error;
    }
  }

  // Get balance sheets
  async getBalanceSheet(ticker) {
    if (!ticker) return [];
    
    const cacheKey = generateCacheKey('balance_sheet_v2', ticker);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached balance sheet data for ${ticker}`);
      return cachedData;
    }
    
    try {
      console.log(`Fetching balance sheet data for ${ticker} from API`);
      const response = await axios.get(`${this.apiBaseUrl}/api/balance-sheet/${ticker}`);
      
      // Cache the data
      setCachedData(cacheKey, response.data, this.cacheDuration.statements);
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching balance sheet data for ${ticker}:`, error);
      throw error;
    }
  }

  // Get key metrics for company - centralized calculation
  async getCompanyMetrics(ticker, period, periodType = 'quarterly') {
    if (!ticker || !period) return null;
    
    const formattedPeriod = this.formatDateForApi(period);
    const cacheKey = generateCacheKey('metrics_v7', ticker, formattedPeriod);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached metrics for ${ticker}, period ${formattedPeriod}`);
      return cachedData;
    }
    
    try {
      // First get financial data needed to calculate metrics
      const financialData = await this.getFinancialData(ticker, false, periodType);
      const targetPeriod = this.formatDateForApi(period);
      const periodData = financialData.find((item) => this.formatDateForApi(item?.period) === targetPeriod);
      
      if (!periodData) {
        console.error(`No data found for period ${period}`);
        return null;
      }
      
      // Calculate metrics
      const metrics = {
        revenue: periodData.Revenue || 0,
        operating_income: periodData['Operating Income'] || 0,
        gross_margin: this.calculateGrossMargin(periodData),
        earnings: this.calculateEarnings(periodData),
        profit_margin: this.calculateProfitMargin(periodData),
        free_cashflow: this.calculateFreeCashflow(periodData),
        assets: 0,
        liabilities: 0,
        cash: 0,
        operating_cashflow: 0,
        cashflow_from_financing: 0,
        cashflow_from_investment: 0,
        change_in_cash_and_cash_equivalents: 0,
        period: period
      };

      // Enrich with balance-sheet metrics used by comparison views.
      try {
        const balanceSheetData = await this.getBalanceSheet(ticker);
        const matchingBalanceRow = this.findBalanceSheetRowForPeriod(balanceSheetData, period);
        if (matchingBalanceRow) {
          metrics.assets = this.toFiniteNumber(matchingBalanceRow.assets);
          metrics.liabilities = this.toFiniteNumber(matchingBalanceRow.liabilities);
          metrics.cash = this.toFiniteNumber(matchingBalanceRow.cash);
        }
      } catch (error) {
        console.error(`Error fetching balance sheet metrics for ${ticker}, period ${period}:`, error);
      }
      
      // Get capital expenditure data from cash flow statement
      try {
        const cashFlowData = await this.getCashFlow(ticker, period);
        const cashFlowRow = Array.isArray(cashFlowData) ? cashFlowData[0] : cashFlowData;

        if (cashFlowRow) {
          metrics.operating_cashflow = this.toFiniteNumber(cashFlowRow.operating_cashflow);
          metrics.cashflow_from_financing = this.toFiniteNumber(cashFlowRow.cashflow_from_financing);
          metrics.cashflow_from_investment = this.toFiniteNumber(cashFlowRow.cashflow_from_investment);
          metrics.change_in_cash_and_cash_equivalents = this.toFiniteNumber(cashFlowRow.change_in_cash_and_cash_equivalents);
          metrics.free_cashflow = this.calculateFreeCashflow({
            ...periodData,
            operating_cashflow: cashFlowRow.operating_cashflow,
            capital_expenditures: cashFlowRow.capital_expenditures,
          });

          // Handle different possible response formats
          if (Array.isArray(cashFlowData) && cashFlowData.length > 0) {
            metrics.capital_expenditures = cashFlowData[0].capital_expenditures || 0;
          } else if (typeof cashFlowData === 'object') {
            metrics.capital_expenditures = cashFlowData.capital_expenditures || 0;
          }
        }
      } catch (error) {
        console.error(`Error fetching capital expenditures for ${ticker}, period ${period}:`, error);
        metrics.capital_expenditures = 0;
      }
      
      // Cache the calculated metrics
      setCachedData(cacheKey, metrics, this.cacheDuration.metrics);
      
      return metrics;
    } catch (error) {
      console.error(`Error calculating metrics for ${ticker}, period ${formattedPeriod}:`, error);
      throw error;
    }
  }
  
  // Helper functions to calculate metrics
  calculateGrossMargin(data) {
    const revenue = this.getFirstFiniteValue(data, ['Revenue', 'revenue']);
    const grossProfit = this.getFirstFiniteValue(data, ['Gross Profit', 'Gross profits', 'gross_profit']);
    const operatingIncome = this.getFirstFiniteValue(data, ['Operating Income', 'operating_income']);

    if (Number.isFinite(grossProfit) && Number.isFinite(revenue) && revenue !== 0) {
      return (grossProfit / revenue) * 100;
    }
    return Number.isFinite(operatingIncome) && Number.isFinite(revenue) && revenue !== 0
      ? (operatingIncome / revenue) * 1.5 * 100
      : 0;
  }
  
  calculateEarnings(data) {
    const netIncome = this.getFirstFiniteValue(data, ['Net Income', 'net_income']);
    if (Number.isFinite(netIncome)) {
      return netIncome;
    }
    const operatingIncome = this.getFirstFiniteValue(data, ['Operating Income', 'operating_income']);
    return Number.isFinite(operatingIncome) ? operatingIncome * 0.8 : 0;
  }
  
  calculateProfitMargin(data) {
    const revenue = this.getFirstFiniteValue(data, ['Revenue', 'revenue']);
    const directNetIncome = this.getFirstFiniteValue(data, ['Net Income', 'net_income']);
    const earnings = Number.isFinite(directNetIncome) ? directNetIncome : this.calculateEarnings(data);
    return Number.isFinite(earnings) && Number.isFinite(revenue) && revenue !== 0 ? (earnings / revenue) * 100 : 0;
  }
  
  calculateFreeCashflow(data) {
    // If we have explicit Free Cash Flow data, use it
    const freeCashFlow = this.getFirstFiniteValue(data, ['Free Cash Flow', 'free_cashflow']);
    if (Number.isFinite(freeCashFlow)) {
      return freeCashFlow;
    }
    
    // If we have operating cash flow and capital expenditures, calculate FCF as OCF - CapEx
    const operatingCashflow = this.getFirstFiniteValue(data, ['operating_cashflow', 'Operating Cash Flow']);
    const capitalExpenditures = this.getFirstFiniteValue(data, ['capital_expenditures', 'Capital Expenditures']);
    if (Number.isFinite(operatingCashflow) && Number.isFinite(capitalExpenditures)) {
      return operatingCashflow - Math.abs(capitalExpenditures);
    }
    
    // Fallback estimation (less accurate)
    const operatingIncome = this.getFirstFiniteValue(data, ['Operating Income', 'operating_income']);
    return Number.isFinite(operatingIncome) ? operatingIncome * 0.85 : 0;
  }

  toFiniteNumber(value) {
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  findBalanceSheetRowForPeriod(balanceSheetData, period) {
    if (!Array.isArray(balanceSheetData) || balanceSheetData.length === 0 || !period) return null;

    const targetFormatted = this.formatDateForApi(period);

    const exactMatch = balanceSheetData.find((row) => this.formatDateForApi(row.period) === targetFormatted);
    if (exactMatch) return exactMatch;

    const targetDate = new Date(targetFormatted);
    if (Number.isNaN(targetDate.getTime())) return null;

    let closest = null;
    let smallestDiff = Infinity;

    balanceSheetData.forEach((row) => {
      const rowDate = new Date(this.formatDateForApi(row.period));
      if (Number.isNaN(rowDate.getTime())) return;
      const diff = Math.abs(rowDate.getTime() - targetDate.getTime());
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closest = row;
      }
    });

    return closest;
  }

  // Clear cache for a specific ticker
  clearTickerCache(ticker) {
    if (!ticker) return;
    
    const prefixes = ['financial_data', 'financial_data_v2', 'financial_data_v3', 'financial_data_v4', 'financial_data_v5', 'financial_data_v27', 'financial_data_v28', 'financial_data_v29', 'financial_data_v30', 'financial_data_v31', 'financial_data_v32', 'financial_data_v33', 'financial_data_v34', 'financial_data_v35', 'financial_data_v36', 'income_statement', 'income_statement_v2', 'income_statement_v3', 'income_statement_v4', 'income_statement_v5', 'income_statement_v6', 'income_statement_v7', 'income_statement_v8', 'cash_flow', 'cash_flow_v4', 'balance_sheet', 'balance_sheet_v2', 'metrics', 'metrics_v4', 'metrics_v5', 'company_overview', 'company_name'];

    // Remove both base keys and period-scoped keys for this ticker.
    const keys = Object.keys(localStorage);
    prefixes.forEach(prefix => {
      const baseKey = generateCacheKey(prefix, ticker);
      clearCacheItem(baseKey);

      keys.forEach(key => {
        if (key.startsWith(`${baseKey}_`)) {
          clearCacheItem(key);
        }
      });
    });
    
    console.log(`Cache cleared for ${ticker}`);
  }

  // Clear all company overview cache to ensure fresh data with description
  clearCompanyOverviewCache() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('company_overview') || key.includes('company_name')) {
        localStorage.removeItem(key);
      }
    });
    console.log('Company overview cache cleared');
  }

  // Get company overview data (sector, industry, address, etc.)
  async getCompanyOverview(ticker, forceRefresh = false) {
    if (!ticker) return null;
    
    const cacheKey = generateCacheKey('company_overview', ticker);
    
    // If forceRefresh is true, skip cache
    if (!forceRefresh) {
      const cachedData = getCachedData(cacheKey);
      
      if (cachedData) {
        console.log(`Using cached company overview data for ${ticker}`);
        return cachedData;
      }
    }
    
    try {
      console.log(`Fetching company overview data for ${ticker} from API`);
      const response = await axios.get(`${this.apiBaseUrl}/api/company-overview/${ticker}`, {
        params: forceRefresh ? { force: 1 } : undefined,
      });
      
      // Cache the data
      setCachedData(cacheKey, response.data, this.cacheDuration.financial);
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching company overview data for ${ticker}:`, error);
      // Return null instead of throwing to prevent UI breaking if this data is missing
      return null;
    }
  }

  // Get company name data
  async getCompanyName(ticker) {
    if (!ticker) return null;
    
    const cacheKey = generateCacheKey('company_name', ticker);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached company name data for ${ticker}`);
      return cachedData;
    }
    
    try {
      console.log(`Fetching company name for ${ticker} from API`);
      const response = await axios.get(`${this.apiBaseUrl}/api/company-name/${ticker}`);
      
      // Cache the data
      setCachedData(cacheKey, response.data, this.cacheDuration.financial);
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching company name for ${ticker}:`, error);
      // Return null instead of throwing to prevent UI breaking if this data is missing
      return null;
    }
  }
}

// Create a singleton instance
const enhancedFinancialDataService = new EnhancedFinancialDataService();
export default enhancedFinancialDataService;
